import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { Lock, CreditCard, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, cartId } = useCart();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  const handleStripeCheckout = async () => {
    if (!email.trim()) {
      setError('Please enter your email for order confirmation.');
      return;
    }
    setProcessing(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/stripe/create-checkout-session`, {
        cart_id: cartId,
        origin_url: originUrl,
        customer_email: email.trim(),
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error initiating payment. Please try again.';
      setError(msg);
      setProcessing(false);
    }
  };

  if (!cart || cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="py-20 md:py-32" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-center gap-2 mb-12">
          <Lock className="w-6 h-6 text-[#B87333]" />
          <h1 className="text-5xl md:text-7xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="checkout-title">
            Checkout
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Order Summary */}
          <div>
            <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Order Summary
            </h2>
            <div className="bg-white/40 border border-[#D7C0A0] p-6 space-y-4" data-testid="order-summary">
              {cart.items.map((item) => (
                <div key={`${item.product_id}-${item.variant || 'default'}`} className="flex justify-between" data-testid={`order-item-${item.product_id}`}>
                  <span className="text-[#5D4037]">
                    {item.name}{item.variant ? ` (${item.variant})` : ''} x {item.quantity}
                  </span>
                  <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-[#D7C0A0] pt-4 mt-4 space-y-2">
                <div className="flex justify-between text-[#5D4037]">
                  <span>Subtotal</span>
                  <span data-testid="checkout-subtotal">${cart.subtotal.toFixed(2)}</span>
                </div>
                {cart.discount > 0 && (
                  <div className="flex justify-between text-[#B87333] font-medium">
                    <span>Discount ({cart.promo_code})</span>
                    <span data-testid="checkout-discount">-${cart.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#5D4037]">
                  <span>Sales Tax (8.25%)</span>
                  <span data-testid="checkout-tax">${(Math.round((cart.subtotal - cart.discount) * 0.0825 * 100) / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#5D4037]">
                  <span>Shipping</span>
                  <span className="text-green-700 text-sm font-medium">Free – Ships from TX</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#D7C0A0]">
                  <span className="text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Total
                  </span>
                  <div className="text-right">
                    {cart.discount > 0 && (
                      <p className="text-sm text-[#5D4037] line-through">
                        ${(cart.subtotal + Math.round(cart.subtotal * 0.0825 * 100) / 100).toFixed(2)}
                      </p>
                    )}
                    <span className="text-2xl font-medium text-[#B87333]" data-testid="checkout-total">
                      ${(Math.round(((cart.subtotal - cart.discount) + (cart.subtotal - cart.discount) * 0.0825) * 100) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div>
            <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment
            </h2>
            <div className="bg-white/40 border border-[#D7C0A0] p-6 space-y-6">
              <div className="flex items-center gap-3 text-[#5D4037]">
                <ShieldCheck className="w-5 h-5 text-[#B87333]" />
                <p className="text-sm">Secure checkout powered by Stripe</p>
              </div>

              <div className="bg-[#FFF8E7] border-l-2 border-[#B87333] p-4" data-testid="stripe-info-notice">
                <p className="text-[#5D4037] text-sm">
                  You'll be redirected to Stripe's secure payment page to complete your purchase.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-[#5D4037]">Email for Order Confirmation *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-white/60 border border-[#D7C0A0] focus:outline-none focus:border-[#B87333] transition-colors"
                  required
                  data-testid="checkout-email-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[#8D7B68] text-xs">
                  <CreditCard className="w-4 h-4" />
                  <span>Visa, Mastercard, AMEX accepted</span>
                </div>
              </div>

              {error && <p className="text-red-600 text-sm" data-testid="checkout-error-message">{error}</p>}

              <button
                onClick={handleStripeCheckout}
                disabled={processing}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="pay-with-stripe-button"
              >
                <Lock className="w-4 h-4" />
                {processing ? 'Redirecting to Stripe...' : `Pay $${(Math.round(((cart.subtotal - cart.discount) + (cart.subtotal - cart.discount) * 0.0825) * 100) / 100).toFixed(2)}`}
              </button>

              <p className="text-center text-xs text-[#8D7B68]">
                By proceeding, you agree to our terms and conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
